import { FC } from "react";
import { Flex, Menu, Portal, Stack, Text } from "@chakra-ui/react";

import { CASE_DOCUMENT_STATUS, CASE_FILE_KIND, CaseDocumentFileModel, CaseDocumentModel } from "@shared/models";
import { CASE_DOCUMENT_INFO } from "@containers/Cases/constants";
import { CaseDocumentActions } from "@containers/Cases/interface";
import { FONT_WEIGHT } from "@assets/theme/foundations/textStyle";

import CaseDocumentFileTag from "./CaseDocumentFileTag";
import CaseDocumentFileMenuItems from "./CaseDocumentFileMenuItems";
import ActionLink from "./CaseDocumentActionLink";
import CaseDocumentCardFrame from "./CaseDocumentCardFrame";
import CaseDocumentCardActions from "./CaseDocumentCardActions";
import CaseDocumentParsingCard from "./CaseDocumentParsingCard";
import CaseDocumentFailedCard from "./CaseDocumentFailedCard";
import { getCaseDocumentMeta, isFailedFile } from "./CaseDocuments.utils";

const DEFAULT_ERROR_MESSAGE = "Couldn't read the files.";

interface ViewLinkProps {
  kind: CASE_FILE_KIND;
  files: CaseDocumentFileModel[];
  onView: (file: CaseDocumentFileModel) => void;
}

const ViewLink: FC<ViewLinkProps> = ({ kind, files, onView }) => {
  if (files.length === 1) {
    return <ActionLink onClick={() => onView(files[0])}>View</ActionLink>;
  }

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <ActionLink>View</ActionLink>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            <CaseDocumentFileMenuItems title={CASE_DOCUMENT_INFO[kind].menuName} files={files} onView={onView} />
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

interface CaseDocumentCardProps {
  caseDocument: CaseDocumentModel;
  actions: CaseDocumentActions;
}

const CardActions: FC<CaseDocumentCardProps> = ({ caseDocument, actions }) => {
  const { kind, status, files } = caseDocument;
  const { view, replace, remove, retry } = actions;
  const isParsing = status === CASE_DOCUMENT_STATUS.PARSING;
  // The PSR's file shows the case's status, so it stays viewable when the case fails.
  const viewableFiles = kind === CASE_FILE_KIND.PSR ? files : files.filter((file) => !isFailedFile(file));
  const viewLink = kind !== CASE_FILE_KIND.MEDICAL && viewableFiles.length > 0 && (
    <ViewLink kind={kind} files={viewableFiles} onView={view} />
  );

  if (kind === CASE_FILE_KIND.PSR) {
    return viewLink;
  }

  const removeLink = (
    <ActionLink color="brand.error" disabled={isParsing || !remove} onClick={() => remove?.(kind)}>
      Remove
    </ActionLink>
  );

  if (status === CASE_DOCUMENT_STATUS.FAILED) {
    return (
      <>
        <ActionLink disabled={!retry} onClick={() => retry?.(kind)}>
          Retry
        </ActionLink>
        <ActionLink disabled={!replace} onClick={() => replace?.(kind)}>
          {CASE_DOCUMENT_INFO[kind].multipleFiles ? "Replace files" : "Replace"}
        </ActionLink>
        {removeLink}
      </>
    );
  }

  return (
    <>
      {viewLink}
      <ActionLink disabled={isParsing || !replace} onClick={() => replace?.(kind)}>
        Replace
      </ActionLink>
      {removeLink}
    </>
  );
};

interface FileItemProps {
  file: CaseDocumentFileModel;
  onView?: (file: CaseDocumentFileModel) => void;
}

const FileItem: FC<FileItemProps> = ({ file, onView }) => {
  const isFailed = isFailedFile(file);

  return (
    <Flex align="center" gap="10px" py="2px" minW={0}>
      <CaseDocumentFileTag file={file} muted={isFailed} />
      <Text truncate flex={1} fontSize="13px" color={isFailed ? "brand.emptyStateText" : "brand.modalHeaderText"}>
        {file.name}
      </Text>
      {isFailed ? (
        <Text flexShrink={0} fontSize="11px" fontWeight={FONT_WEIGHT.BOLD} color="score.lowText">
          COULDN’T READ
        </Text>
      ) : (
        onView && (
          <ActionLink flexShrink={0} onClick={() => onView(file)}>
            View
          </ActionLink>
        )
      )}
    </Flex>
  );
};

const CaseDocumentDefaultCard: FC<CaseDocumentCardProps> = ({ caseDocument, actions }) => {
  const { kind, status, files, error_message } = caseDocument;
  const isDocumentFailed = status === CASE_DOCUMENT_STATUS.FAILED;
  const listedFiles = [...files.filter((file) => !isFailedFile(file)), ...files.filter(isFailedFile)];
  const { chooseValues } = actions;

  return (
    <CaseDocumentCardFrame caseDocument={caseDocument} onChooseValues={chooseValues && (() => chooseValues())}>
      <Stack gap={1} px={3} py={2} borderRadius="8px" bg="brand.bg">
        {listedFiles.map((file) => (
          <FileItem key={file.id} file={file} onView={kind === CASE_FILE_KIND.MEDICAL ? actions.view : undefined} />
        ))}
      </Stack>

      {isDocumentFailed && kind !== CASE_FILE_KIND.PSR && (
        <Text fontSize="13px" color="brand.error">
          {error_message || DEFAULT_ERROR_MESSAGE}
        </Text>
      )}

      <Text fontSize="12px" color="brand.emptyStateText">
        {getCaseDocumentMeta(caseDocument)}
      </Text>

      <CaseDocumentCardActions>
        <CardActions caseDocument={caseDocument} actions={actions} />
      </CaseDocumentCardActions>
    </CaseDocumentCardFrame>
  );
};

const CaseDocumentCard: FC<CaseDocumentCardProps> = (props) => {
  const { kind, status } = props.caseDocument;

  if (kind === CASE_FILE_KIND.MEDICAL && status === CASE_DOCUMENT_STATUS.PARSING) {
    return <CaseDocumentParsingCard {...props} />;
  }
  if (kind === CASE_FILE_KIND.MEDICAL && status === CASE_DOCUMENT_STATUS.FAILED) {
    return <CaseDocumentFailedCard {...props} />;
  }

  return <CaseDocumentDefaultCard {...props} />;
};

export default CaseDocumentCard;
